<template>
    <v-form
        v-model="valid"
        ref="form"
        :disabled="disabled || isSubmitting"
        :loading="isLoadingUsers"
    >
        <v-row class="ma-0">
            <v-col cols="12" class="pa-0">
                <v-autocomplete
                    v-model="user_id"
                    :label="isLoadingUsers
                            ? 'Obtendo lista de colaboradores...'
                            : (
                                isLoadingRequestInformation
                                ? 'Obtendo informações do Pedido'
                                : 'Colaborador ausente'
                            )
                    "
                    :items="usersList"
                    :item-title="(item) => `${item?.worker_code ? item.worker_code + ' - ' : ''}${item?.name ?? item?.email ?? item?.username ?? 'ERRO: Nome indisponível'}`"
                    item-value="id"
                    :disabled="disabled || isLoadingUsers || isSubmitting || isLoadingRequestInformation"
                    :readonly="props.readonly || !!props?.request || !canSelectEmployee"
                    :loading="isLoadingUsers || isLoadingRequestInformation"
                    :clearable="!props.readonly && !request"
                    :menu-icon="props.readonly || !!request ? '' : undefined"
                    :rules="['create', 'edit', 'update'].includes(action)
                            ? [formValidationRules.required(user_id)]
                            : []
                    "
                    @update:model-value="updateDepartmentForSelectedUser"
                    autocomplete="off"
                />
            </v-col>
        </v-row>

        <v-row class="ma-0">
            <v-col cols="12" class="pa-0">
                <v-autocomplete
                    v-model="department_id"
                    label="Centro de Custos"
                    :items="departmentsList"
                    :item-title="(dep) => `${dep?.external_key ? dep.external_key + ' - ' : ''}${dep?.name ?? 'Centro de Custo sem Nome'}`"
                    item-value="id"
                    :disabled="disabled || isSubmitting || isLoadingRequestInformation || isLoadingDepartments"
                    :loading="isLoadingRequestInformation || isLoadingDepartments"
                    menu-icon=""
                    readonly
                    autocomplete="off"
                />
            </v-col>
        </v-row>

        <v-row class="ma-0">
            <v-col cols="12" class="pa-0">
                <v-date-input
                    v-model="start_date"
                    label="Data de início"
                    prepend-icon=""
                    :disabled="disabled || isSubmitting || isLoadingRequestInformation"
                    :readonly="props.readonly || (!!request && !['edit', 'update'].includes(action))"
                    :clearable="!props.readonly && !(!!request && !['edit', 'update'].includes(action))"
                    :menu-icon="props.readonly || !!request ? '' : undefined"
                    :min="new Date()"
                    :rules="['create', 'edit', 'update'].includes(action)
                                ? [
                                    formValidationRules.required(start_date),
                                    formValidationRules.valueMustBeDifferent(
                                        start_date,
                                        return_to_work_date
                                    ),
                                ]
                                : []
                    "
                    autocomplete="off"
                />
            </v-col>
        </v-row>

        <v-row class="ma-0">
            <v-col cols="12" class="pa-0">
                <v-number-input
                    v-model="number_of_days"
                    :control-variant="['create', 'edit', 'update'].includes(action) ? 'split' : 'hidden'"
                    :min="Number(1)"
                    label="Número de dias"
                    :disabled="disabled || isSubmitting || isLoadingRequestInformation"
                    :readonly="props.readonly || (!!request && !['edit', 'update'].includes(action))"
                    :clearable="!props.readonly && !(!!request && !['edit', 'update'].includes(action))"
                    :menu-icon="props.readonly || !!request ? '' : undefined"
                    :rules="['create', 'edit', 'update'].includes(action)
                            ? [formValidationRules.required(number_of_days)]
                            : []
                    "
                    autocomplete="off"
                />
            </v-col>
        </v-row>

        <v-row class="ma-0">
            <v-col cols="12" class="pa-0">
                <v-date-input
                    v-model="end_date"
                    label="Data de fim"
                    prepend-icon=""
                    :disabled="disabled || isSubmitting || isLoadingRequestInformation"
                    :readonly="props.readonly || (!!request && !['edit', 'update'].includes(action))"
                    :clearable="!props.readonly && !(!!request && !['edit', 'update'].includes(action))"
                    :menu-icon="props.readonly || !!request ? '' : undefined"
                    :min="start_date ?? new Date()"
                    :rules="['create', 'edit', 'update'].includes(action)
                            ? [
                                formValidationRules.required(end_date),
                                formValidationRules.valueMustBeDifferent(
                                    end_date,
                                    return_to_work_date
                                ),
                            ]
                            : []
                    "
                    autocomplete="off"
                />
            </v-col>
        </v-row>

        <v-row class="ma-0">
            <v-col cols="12" class="pa-0">
                <v-date-input
                    v-model="return_to_work_date"
                    label="Data de regresso ao trabalho"
                    prepend-icon=""
                    :disabled="disabled || isSubmitting || isLoadingRequestInformation"
                    :readonly="props.readonly || (!!request && !['edit', 'update'].includes(action))"
                    :clearable="!props.readonly && !(!!request && !['edit', 'update'].includes(action))"
                    :menu-icon="props.readonly || !!request ? '' : undefined"
                    :min="end_date ?? new Date()"
                    :rules="['create', 'edit', 'update'].includes(action)
                            ? [
                                formValidationRules.required(return_to_work_date),
                                formValidationRules.valueCannotBelongTo(
                                    return_to_work_date,
                                    [start_date, end_date]
                                ),
                            ]
                            : []
                    "
                    autocomplete="off"
                />
            </v-col>
        </v-row>

        <AbsenceRequestStatusFields
            v-if="props?.action && !['create', 'edit', 'update', 'history', 'cancel'].includes(props?.action)"
            :action="action"
            :status-information="requestData"
            :readonly="props?.readonly"
            :disabled="props?.disabled || isLoadingRequestInformation"
            :is-submitting="isSubmitting"
            v-model:reason="reason"
        />

        <AbsenceRequestStatusFields
            v-for="previousAction in cancellationPreviousActions"
            :key="previousAction"
            :action="previousAction"
            :status-information="requestData"
            :readonly="true"
            :disabled="props.disabled || isLoadingRequestInformation"
            :is-submitting="isSubmitting"
            :as-history="true"
        />

        <AbsenceRequestStatusFields
            v-if="props.action === 'cancel'"
            action="cancel"
            :status-information="requestData"
            :disabled="props.disabled || isLoadingRequestInformation"
            :is-submitting="isSubmitting"
            v-model:reason="reason"
        />

        <template v-if="props.action === 'history'">
            <AbsenceRequestStatusFields
                v-for="historyAction in historyActions"
                :key="historyAction"
                :action="historyAction"
                :status-information="requestData"
                :readonly="true"
                :disabled="props.disabled || isLoadingRequestInformation"
                :is-submitting="isSubmitting"
                :as-history="true"
            />
        </template>
    </v-form>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import formValidationRules from '../../../composables/formValidationRules';
import AbsenceRequest from '../../../api/absences/requests/AbsenceRequests';
import User from '@/api/User';
import Department from '@/api/departments/Department';
import { useSessionStore } from '@/stores/SessionStore';
import AbsenceRequestStatusFields from './AbsenceRequestStatusFields.vue';
import { getDate } from '../../../composables/datetime.js';

const props = defineProps({
    disabled: {
        type: Boolean,
        default: false,
    },
    readonly: {
        type: Boolean,
        default: false,
    },
    onSubmit: {
        type: Function,
        default: null,
    },
    request: {
        type: Object,
        default: null,
    },
    action: {
        type: String,
        default: 'create',
    },
    idempotency_key: {
        type: String,
        default: null,
    },
});

defineExpose({
    validate,
    submit,
});

const form = ref('form');
const valid = ref(false);
const usersList = ref([]);
const departmentsList = ref([]);
const isLoadingUsers = ref(false);
const isLoadingDepartments = ref(false);
const isLoadingRequestInformation = ref(false);
const isSubmitting = ref(false);
const sessionStore = useSessionStore();

// form fields
const user_id = ref(props?.request?.user_id ?? null);
const department_id = ref(props?.request?.department_id ?? null);
const start_date = ref(
    props?.request?.start_date
        ? getDate(props.request.start_date)
        : null
);
const number_of_days = ref(props?.request?.number_of_days ?? null);
const end_date = ref(
    props?.request?.end_date
        ? getDate(props.request.end_date)
        : null
);
const return_to_work_date = ref(
    props?.request?.return_to_work_date
        ? getDate(props.request.return_to_work_date)
        : null
);
const reason = ref(null);

const actionIdempotencyKey =
    props.idempotency_key
    ?? self?.crypto?.randomUUID()
    ?? null;

const requestData = ref(props?.request);

const historyActions = computed(() => {
    if (props.action !== 'history' || !requestData.value) {
        return [];
    }

    return [
        'create',
        requestData.value.approved_at ? 'approve' : null,
        requestData.value.cancelled_at ? 'cancel' : null,
        requestData.value.denied_at ? 'deny' : null,
        requestData.value.processed_at ? 'process' : null,
        requestData.value.deleted_at ? 'delete' : null,
    ].filter(Boolean);
});

const cancellationPreviousActions = computed(() => {
    if (props.action !== 'cancel' || !requestData.value) {
        return [];
    }

    return [
        'create',
        requestData.value.approved_at ? 'approve' : null,
        requestData.value.processed_at ? 'process' : null,
    ].filter(Boolean);
});

async function validate() {
    const result = await form.value.validate();

    return result.valid;
}

async function submit() {
    isSubmitting.value = true;

    const shouldValidate = [
        'create',
        'update',
        'edit',
        'reject',
        'deny',
        'delete',
        'cancel',
    ].includes(props.action) && !props.disabled;

    const formValues = resolveFormValues();

    try {
        if (shouldValidate) {
            const passedValidation = await validate();

            if (!passedValidation) {
                console.warn(
                    'Validação falhou no AbsenceRequestForm',
                    formValues
                );

                return false;
            }
        }

        if (props.onSubmit) {
            return await props.onSubmit(formValues);
        }

        return await AbsenceRequest.create(formValues);
    } catch (error) {
        console.error(
            'Erro no submit AbsenceRequestForm:',
            error
        );

        throw error;
    } finally {
        isSubmitting.value = false;
    }
}

const selectedUser = computed(() => {
    return usersList.value.find(
        (user) => user.id === user_id.value
    ) ?? null;
});

const canSelectEmployee = computed(() => {
    return sessionStore.hasPermission([
        'absence.request.create.any',
        'absence.request.create.department',
    ]);
});

/**
 * Converte uma data para YYYY-MM-DD usando a data LOCAL.
 *
 * Não usamos toISOString(), porque isso converte a data para UTC.
 *
 * Exemplo em Portugal durante o horário de verão:
 *
 * 2026-08-31 00:00 (+01:00)
 *
 * com toISOString():
 * 2026-08-30T23:00:00.000Z
 *
 * o que faria o backend receber 2026-08-30.
 */
function formatDateForApi(value) {
    if (!value) {
        return null;
    }

    /*
     * Se já vier no formato YYYY-MM-DD,
     * devolvemos diretamente.
     */
    if (
        typeof value === 'string'
        && /^\d{4}-\d{2}-\d{2}$/.test(value)
    ) {
        return value;
    }

    /*
     * Se vier uma string datetime, por exemplo:
     *
     * 2026-08-31T00:00:00
     *
     * não queremos simplesmente usar substring se existir timezone.
     * Transformamos numa Date e usamos os componentes locais.
     */
    let date = value;

    if (!(date instanceof Date)) {
        date = new Date(value);
    }

    if (Number.isNaN(date.getTime())) {
        console.warn(
            'Data inválida recebida em formatDateForApi:',
            value
        );

        return null;
    }

    const year = date.getFullYear();

    const month = String(
        date.getMonth() + 1
    ).padStart(2, '0');

    const day = String(
        date.getDate()
    ).padStart(2, '0');

    return `${year}-${month}-${day}`;
}

function resolveFormValues() {
    const payload = {
        idempotency_key: actionIdempotencyKey,
    };

    if (
        ['create', 'update', 'edit'].includes(
            props.action
        )
    ) {
        Object.assign(payload, {
            department_id:
                department_id.value,

            start_date:
                formatDateForApi(
                    start_date.value
                ),

            number_of_days:
                number_of_days.value,

            end_date:
                formatDateForApi(
                    end_date.value
                ),

            return_to_work_date:
                formatDateForApi(
                    return_to_work_date.value
                ),
        });
    }

    if (props.action === 'create') {
        Object.assign(payload, {
            user_id: user_id.value,
        });
    } else if (props.request) {
        payload.id = props.request.id;
    }

    if (
        ['reject', 'deny', 'delete', 'cancel'].includes(
            props.action
        )
    ) {
        payload.reason = reason.value;
    }

    return payload;
}

function updateDepartmentForSelectedUser() {
    department_id.value =
        selectedUser.value?.paid_by_department_id
        ?? null;
}

async function fetchFormData() {
    await Promise.all([
        fetchUsersList(),
        fetchDepartmentsList(),
    ]);
}

async function getAbsenceRequest() {
    const method = [
        'update',
        'approve',
        'deny',
        'reject',
    ].includes(props?.action)
        ? 'show'
        : (
            [
                'history',
                'process',
                'delete',
                'cancel',
            ].includes(props?.action)
                ? 'history'
                : null
        );

    if (!method) {
        return;
    }

    isLoadingRequestInformation.value = true;

    AbsenceRequest?.[method](
        props?.request?.id
    )
        .then((response) => {
            requestData.value = {
                ...requestData.value,
                ...(response?.data ?? {}),
            };

            usersList.value = [
                requestData.value.user,
            ].filter(Boolean);

            user_id.value =
                requestData.value?.user?.id
                ?? requestData.value?.user_id;

            departmentsList.value = [
                requestData.value.department
                ?? {
                    id:
                        requestData.value.department_id,

                    external_key:
                        null,

                    name:
                        requestData.value.department_id,
                },
            ];

            /*
             * Importante:
             * carregamos as três datas como Date para os v-date-input.
             */
            start_date.value =
                requestData.value?.start_date
                    ? getDate(
                        requestData.value.start_date
                    )
                    : null;

            end_date.value =
                requestData.value?.end_date
                    ? getDate(
                        requestData.value.end_date
                    )
                    : null;

            return_to_work_date.value =
                requestData.value?.return_to_work_date
                    ? getDate(
                        requestData.value.return_to_work_date
                    )
                    : null;

            number_of_days.value =
                requestData.value?.number_of_days
                ?? null;

            department_id.value =
                requestData.value?.department?.id
                ?? requestData.value?.department_id
                ?? null;
        })
        .catch((error) => {
            console.error(
                'Ocorreu um erro a obter as informações do Pedido de Ausência',
                String(error)
            );

            throw error;
        })
        .finally(() => {
            isLoadingRequestInformation.value = false;
        });
}

async function fetchUsersList() {
    isLoadingUsers.value = true;

    try {
        const sessionUser =
            sessionStore.user
            ?? (
                await User.getSession()
            ).data;

        const canCreateForDepartment =
            sessionStore.hasPermission(
                'absence.request.create.department'
            );

        const canCreateForAny =
            sessionStore.hasPermission(
                'absence.request.create.any'
            );

        if (
            canCreateForAny
            || canCreateForDepartment
        ) {
            const response =
                await User.list({
                    scope_to_current_user_departments: 1,
                });

            const users =
                response.data
                ?? [];

            usersList.value = [
                sessionUser,

                ...users.filter(
                    (user) =>
                        user.id
                        !== sessionUser?.id
                ),
            ].filter(Boolean);
        } else {
            usersList.value = [
                sessionUser,
            ].filter(Boolean);

            user_id.value =
                sessionUser?.id
                ?? null;
        }
    } finally {
        isLoadingUsers.value = false;
    }
}

async function fetchDepartmentsList() {
    isLoadingDepartments.value = true;

    try {
        const response =
            await Department.all({
                all: 1,
            });

        departmentsList.value =
            response.data
            ?? [];
        
        if(user_id.value) {

            department_id.value = usersList.value?.find((u) => u.id == user_id.value)?.paid_by_department_id;
        }
    } finally {
        isLoadingDepartments.value = false;
    }
}

onMounted(() => {
    if (!props.request) {
        fetchFormData();

        return;
    }

    getAbsenceRequest();
});
</script>

<style scoped>
</style>